import type { HostCommand, Lobby as LobbyState } from '@lafayette/protocol';
import { useState } from 'react';

import { SettingsMenu } from './SettingsMenu.js';
import styles from './Lobby.module.css';

export interface LobbyProps {
  lobby: LobbyState;
  viewerId: string | null;
  onHostCommand: (command: HostCommand) => void;
}

export function Lobby({ lobby, viewerId, onHostCommand }: LobbyProps) {
  const [copied, setCopied] = useState(false);
  const isHost = viewerId !== null && viewerId === lobby.hostPlayerId;
  const seated = lobby.seats.length;
  const canStart = isHost && seated >= lobby.minPlayers;

  async function copyInvite(): Promise<void> {
    if (!lobby.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(lobby.inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  }

  return (
    <main className={styles.page}>
      <section className={styles.panel} aria-labelledby="lobby-heading">
        <header className={styles.header}>
          <h1 id="lobby-heading" className={styles.title}>
            Lobby
          </h1>
          <p className={styles.subtitle}>
            {seated} of {lobby.maxPlayers} seats &middot; {lobby.minPlayers} needed to start
          </p>
          <SettingsMenu />
        </header>

        {isHost && lobby.inviteUrl ? (
          <div className={styles.invite}>
            <label className={styles.label} htmlFor="invite">
              Secret invite link
            </label>
            <div className={styles.inviteRow}>
              <input id="invite" className="lf-input" readOnly value={lobby.inviteUrl} />
              <button type="button" className="lf-button lf-button--small" onClick={copyInvite}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <p className={styles.hint}>
              Anyone with this link can take a seat. It does not give away your seat.
            </p>
          </div>
        ) : null}

        <ul className={styles.seats}>
          {lobby.seats.map((seat) => (
            <li key={seat.playerId} className={styles.seat} data-seat={seat.name}>
              <span
                className={`${styles.dot} ${seat.connected ? styles.dotOn : styles.dotOff}`}
                aria-hidden="true"
              />
              <span className={styles.seatName}>{seat.name}</span>
              <span className={styles.tags}>
                {seat.isHost ? <span className={styles.tag}>Host</span> : null}
                {seat.playerId === viewerId ? <span className={styles.tag}>You</span> : null}
                {!seat.connected ? <span className={styles.tagMuted}>Away</span> : null}
              </span>
              {isHost && seat.playerId !== lobby.hostPlayerId ? (
                <button
                  type="button"
                  className="lf-button lf-button--small"
                  onClick={() => onHostCommand({ type: 'removeSeat', playerId: seat.playerId })}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
          {Array.from({ length: Math.max(0, lobby.maxPlayers - seated) }, (_, i) => (
            <li key={`open-${i}`} className={`${styles.seat} ${styles.seatOpen}`}>
              <span className={styles.seatName}>Open seat</span>
            </li>
          ))}
        </ul>

        {isHost ? (
          <div className={styles.controls}>
            <button
              type="button"
              className="lf-button"
              onClick={() => onHostCommand({ type: 'setLocked', locked: !lobby.locked })}
            >
              {lobby.locked ? 'Unlock lobby' : 'Lock lobby'}
            </button>
            <button
              type="button"
              className="lf-button lf-button--primary"
              onClick={() => onHostCommand({ type: 'startMatch' })}
              disabled={!canStart}
            >
              Start match
            </button>
          </div>
        ) : (
          <p className={styles.waiting} role="status">
            Waiting for the host to start the match.
          </p>
        )}
      </section>
    </main>
  );
}
